import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CategoriesService from "@/services/categories.service";

interface Option {
  value: string;
  label: string;
}

interface CategoryInfiniteSelectProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  selectedOptionFallback?: Option | null;
}

const LIMIT = 10;

export default function CategoryInfiniteSelect({
  value = "",
  onChange,
  placeholder = "Select category",
  className = "",
  selectedOptionFallback = null,
}: CategoryInfiniteSelectProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchCategories = useCallback(async (nextPage: number, query: string, replaceData = false) => {
    try {
      setIsLoading(true);
      const response = await CategoriesService.getCategories({
        page: nextPage,
        limit: LIMIT,
        search: query,
      });

      const categories = response.data.categories || [];
      const mapped = categories.map((cat: any) => ({
        label: cat.title,
        value: cat._id,
      }));

      setOptions((prev) => {
        const base = replaceData ? [] : prev;
        const merged = [...base, ...mapped];
        const deduped = new Map<string, Option>();
        merged.forEach((item) => deduped.set(item.value, item));
        return Array.from(deduped.values());
      });

      setHasMore(categories.length >= LIMIT);
    } catch (error) {
      console.error("Error fetching categories:", error);
      if (replaceData) {
        setOptions([]);
      }
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchCategories(1, searchQuery, true);
  }, [searchQuery, fetchCategories]);

  useEffect(() => {
    if (page === 1) return;
    fetchCategories(page, searchQuery);
  }, [page, searchQuery, fetchCategories]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = useMemo(() => {
    if (!value) return null;

    const fromOptions = options.find((item) => item.value === value);
    if (fromOptions) return fromOptions;

    if (selectedOptionFallback && selectedOptionFallback.value === value) {
      return selectedOptionFallback;
    }

    return {
      value,
      label: value,
    };
  }, [options, selectedOptionFallback, value]);

  const handleOptionClick = (option: Option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const hitBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 10;

    if (hitBottom && hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-left text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 ${
          selectedOption ? "text-gray-800 dark:text-white/90" : "text-gray-400 dark:text-gray-400"
        }`}
      >
        <span className="block truncate">{selectedOption?.label || placeholder}</span>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search category..."
            className="mb-2 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />

          <div className="max-h-60 overflow-y-auto" onScroll={handleScroll}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleOptionClick(option)}
                className={`flex w-full items-center rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  value === option.value ? "bg-gray-100 dark:bg-gray-800" : ""
                }`}
              >
                {option.label}
              </button>
            ))}

            {!isLoading && options.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No categories found</div>
            )}

            {isLoading && (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Loading...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
